import React, { useEffect, useState } from 'react'

import { Link } from 'react-router-dom'

import { AuthorsList } from '../common/AuthorsList'
import { Pagination } from '../common/Pagination'
import { getData } from '../services/HomePage'
import { SearchForm } from './components/SearchForm'
import { SearchResult } from './dataStructures'

interface DateRange {
  start: string,
  end: string
}

export interface SearchParams {
  searchText: string,
  authors: any[],
  publicationYear?: DateRange,
  temporalCoverage?: DateRange,
  edmedText: string
}

export interface Ordering {
  columnName: string,
  direction: string
}

export const HomePage = () => {
  const [isLoading, setIsLoading] = useState(false)
  const [results, setResults] = useState<SearchResult[]>([])
  const [page, setPage] = useState(1)
  const [ordering, setOrdering] = useState<Ordering>({ columnName: 'publicationDate', direction: 'desc' })
  const [pageCount, setPageCount] = useState(1)
  const [totalResults, setTotalResults] = useState(1)
  const [searchParams, setSearchParams] = useState<SearchParams>({ searchText: '', authors: [], edmedText: '' })

  const loadData = async () => {
    setIsLoading(true)
    const r = await getData(page, searchParams, ordering)
    setResults(r.results)
    setPageCount(r.pageCount)
    setTotalResults(r.totalResults)
    setIsLoading(false)
  }

  const changePage = (pageNumber: number) => {
    setPage(pageNumber)
  }

  const changeOrdering = (name: string) => {
    let direction = 'asc'
    if (ordering?.columnName === name && ordering?.direction === 'asc') {
      direction = 'desc'
    }
    setOrdering({ columnName: name, direction })
  }

  useEffect(() => {
    loadData()
  }, [page, ordering])

  const Header = ({ title, name, sortable = false }: {title: string, name: string, sortable?: boolean}) => {
    const onClick = () => {
      if (!sortable) return
      changeOrdering(name)
    }

    let arrow = '&updownarrow;'
    if (ordering?.columnName === name) {
      if (ordering?.direction === 'asc') {
        arrow = '&uparrow;'
      } else {
        arrow = '&downarrow;'
      }
    }

    return (
      <th onClick={onClick} style={sortable ? { cursor: 'pointer' } : {}}>
        <span style={sortable ? { textDecoration: 'underline' } : {}}>
          {title}
        </span>
        {sortable &&
          <span
            style={{ marginLeft: '5px', fontSize: '1.2em' }}
            dangerouslySetInnerHTML={{ __html: arrow }}
          />
        }
      </th>
    )
  }

  return (
    <div className='home'>
      <p>
        This catalogue provides access to snapshots of specially chosen datasets that are archived using rigorous version management. This enables citation in journal papers through the assignment of a Digital Object Identifier (DOI) in collaboration with the British Library.
      </p>
      <p>
        Not all data held at BODC have been allocated DOIs. The data within a DOI are freely available.  Please see where to find data for more information about BODC&apos;s other data holdings.
      </p>

      <h2>Your search criteria</h2>

      <SearchForm
        params={searchParams}
        setParams={setSearchParams}
        loadData={loadData}
        resetPage={() => setPage(1)}
      />

      <h2>Results table</h2>

      <div style={{ width: 'calc(100% + 400px)' }}>

        <Pagination currentPage={page} changePage={changePage} maxPage={pageCount} totalResults={totalResults} />

        <div id='resultTableDiv'>
          <table id='tabofres' className='table1 tablesorter'>
            <thead>
              <tr>
                <Header name='title' title='Summary Title' sortable />
                <Header name='authors' title='Author(s)' sortable />
                <Header name='publicationDate' title='Publication Date' sortable />
                <Header name='temporalCoverage' title='Temporal coverage' sortable />
                <Header name='edmed' title='EDMED' sortable />
                <Header name='doi' title='Digital Object Identifier (DOI)' />
              </tr>
            </thead>
            <tbody>
              {isLoading &&
                <tr>
                  <td colSpan={100}>
                    Loading...
                  </td>
                </tr>
              }
              {!isLoading && results.map(x =>
                <tr key={x.doi}>
                  <td id='title' dangerouslySetInnerHTML={{ __html: x.title }} />
                  <td id='authors'>
                    <strong>
                      {x.citationType === 'A' && <>Authors:</>}
                      {x.citationType === 'C' && <>Group Author:</>}
                    </strong>
                    {' '}
                    <AuthorsList authors={x.authors.map(x => ({ name: x }))} />
                    {x.contributors.length !== 0 &&
                    <>
                      <br/>
                      <strong>Contributors:</strong>
                      {' '}
                      <AuthorsList authors={x.contributors.map(x => ({ name: x }))} />
                    </>
                    }
                  </td>
                  <td id='pubDate'>{x.publicationDate.format('YYYY')}</td>
                  <td id='tempCoverage'>
                    {x.temporalCoverage.start.format(x.tempCoverageMasks.start)} to {x.temporalCoverage.end.format(x.tempCoverageMasks.end)}
                  </td>
                  <td id='edmed'>
                    <a href={`https://edmed.seadatanet.org/report/${x.edmedId}/`} target='_blank' rel='noreferrer'>
                      {x.edmed}
                    </a>
                  </td>
                  <td id='doi'>
                    <Link to={`catalogue/${x.doi.split(':')[1]}`}>
                      {x.doi}
                    </Link>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <Pagination currentPage={page} changePage={changePage} maxPage={pageCount} totalResults={totalResults} />
      </div>
    </div>
  )
}
